import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Grid,
  MenuItem,
  TextField,
  Divider,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import { useAppSelector, useAppDispatch } from "../../../../redux/store";
import { chargeCart } from "../../../../redux/slices/invoicesSlice";
import { openProjectDrawer } from "../../../../redux/slices/projectsSlice";

// Amount left to pay for a pending invoice. Pending invoices carry
// calculatedAmountDue (amountDue - scheduledAmount) from the server; fall
// back to deriving it so the dialog works regardless of payload shape.
const payableAmount = (invoice: any) =>
  invoice?.calculatedAmountDue ??
  (invoice?.amountDue != null && invoice?.scheduledAmount != null
    ? invoice.amountDue - invoice.scheduledAmount
    : invoice?.amount ?? 0);

interface CartPaymentDialogProps {
  open: boolean;
  invoices: any[];
  project: any;
  onClose: () => void;
}

const CartPaymentDialog = ({
  open,
  invoices,
  project,
  onClose,
}: CartPaymentDialogProps) => {
  const dispatch = useAppDispatch();
  const { cartChargeStatus } = useAppSelector((state) => state.invoices) as any;

  const bankAccounts = project?.bankAccounts || [];
  const [accountId, setAccountId] = useState<string>("");
  const [authorized, setAuthorized] = useState(false);

  // Reset the selected account whenever the dialog opens for a project.
  useEffect(() => {
    if (open) {
      setAccountId(bankAccounts[0]?.accountID || "");
      setAuthorized(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, project?.providerId]);

  const selectedAccount = bankAccounts.find(
    (account: any) => account.accountID === accountId,
  );
  const last4 = String(selectedAccount?.accountNumber || "")
    .replace(/\D/g, "")
    .slice(-4);

  const total = useMemo(
    () => invoices.reduce((sum, inv) => sum + payableAmount(inv), 0),
    [invoices],
  );

  const loading = cartChargeStatus === "loading";

  const handleConfirm = useCallback(async () => {
    if (!project?.providerId || !accountId || invoices.length === 0) return;
    try {
      await dispatch(
        chargeCart({
          customerId: project.providerId,
          accountId,
          payments: invoices.map((inv) => ({
            invoiceId: inv.id,
            amount: payableAmount(inv),
          })),
        }),
      ).unwrap();
    } catch {
      // Error surfaces via the snackbar watcher in the parent.
    }
  }, [dispatch, project?.providerId, accountId, invoices]);

  return (
    <Dialog
      open={open}
      maxWidth="sm"
      component="form"
      fullWidth
      onClose={loading ? undefined : onClose}
      sx={{
        "& .MuiDialog-paper": { borderRadius: 0, p: 1 },
        "& .MuiOutlinedInput-root": { borderRadius: 0 },
      }}
    >
      <DialogTitle>Pay Selected Invoices</DialogTitle>
      <DialogContent>
        {invoices.length === 0 ? (
          <Alert severity="info">No invoices selected.</Alert>
        ) : (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {project?.name} · {invoices.length}{" "}
              {invoices.length === 1 ? "invoice" : "invoices"}
            </Typography>
            <Box sx={{ maxHeight: 260, overflowY: "auto" }}>
              {invoices.map((inv) => (
                <Box
                  key={inv.id}
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    py: 1,
                  }}
                >
                  <Typography variant="body2">
                    {inv.invoiceNumber || inv.id}
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {payableAmount(inv).toLocaleString("en-US", {
                      style: "currency",
                      currency: "USD",
                    })}
                  </Typography>
                </Box>
              ))}
            </Box>
            <Divider sx={{ my: 1.5 }} />
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}
            >
              <Typography sx={{ fontWeight: 700 }}>Total</Typography>
              <Typography sx={{ fontWeight: 700 }}>
                {total.toLocaleString("en-US", {
                  style: "currency",
                  currency: "USD",
                })}
              </Typography>
            </Box>

            {bankAccounts.length === 0 ? (
              <Alert severity="warning">
                <Typography variant="body2">
                  No bank account linked to this project. Add one to pay
                  multiple invoices at once.
                </Typography>
                <Button
                  variant="outlined"
                  color="warning"
                  size="small"
                  sx={{ mt: 1 }}
                  onClick={() => {
                    onClose();
                    dispatch(openProjectDrawer(project));
                  }}
                >
                  Project Settings
                </Button>
              </Alert>
            ) : (
              <Grid container rowSpacing={1.5}>
                <Grid size={12}>
                  <Typography variant="subtitle2">Select Account</Typography>
                  <TextField
                    select
                    fullWidth
                    required
                    size="small"
                    value={accountId}
                    onChange={(e) => setAccountId(e.target.value)}
                    disabled={loading}
                  >
                    {bankAccounts.map((account: any) => (
                      <MenuItem
                        key={account.accountID}
                        value={account.accountID}
                      >
                        {account.accountName} - {account.accountNumber}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
              </Grid>
            )}

            {accountId && bankAccounts.length > 0 && (
              <Box
                sx={{
                  mt: 2,
                  p: 2,
                  border: "1px solid var(--tpc-200)",
                  backgroundColor: "var(--surface-container-low, #f7f7f7)",
                }}
              >
                <Typography variant="body2">
                  One ACH payment of{" "}
                  <strong>
                    {total.toLocaleString("en-US", {
                      style: "currency",
                      currency: "USD",
                    })}
                  </strong>{" "}
                  from{" "}
                  <strong>
                    {selectedAccount?.accountName || "the selected account"}
                    {last4 ? ` ending ${last4}` : ""}
                  </strong>{" "}
                  will be applied to {invoices.length}{" "}
                  {invoices.length === 1 ? "invoice" : "invoices"} when you
                  confirm.
                </Typography>
                <FormControlLabel
                  sx={{ mt: 1 }}
                  control={
                    <Checkbox
                      checked={authorized}
                      onChange={(event) =>
                        setAuthorized(event.target.checked)
                      }
                      disabled={loading}
                    />
                  }
                  label={
                    <Typography variant="body2" sx={{ fontSize: 12 }}>
                      I authorize The Pinnacle Companies to debit this account
                      for the amount shown.
                    </Typography>
                  }
                />
              </Box>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          type="submit"
          variant="contained"
          color="success"
          disabled={
            loading || !accountId || !authorized || invoices.length === 0
          }
          onClick={handleConfirm}
        >
          {loading ? "Processing..." : "Confirm & Pay"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CartPaymentDialog;
